/**
 * Permission system for Folio
 *
 * Permission format: "module:submodule:action"
 * - module: Top-level feature area (contabilidad, facturacion, reportes, etc.)
 * - submodule: Specific feature within module (diario, cuentas, facturas, etc.)
 * - action: read, write, delete, admin
 *
 * Wildcards:
 * - "*" matches any segment
 * - "contabilidad:*:read" = read access to all contabilidad submodules
 * - "*:*:read" = read access to everything
 */
// Action types
export const ACTIONS = ["read", "write", "delete", "admin"];
// Module definitions with their submodules
export const MODULES = {
    contabilidad: {
        label: "Contabilidad",
        submodules: {
            diario: { label: "Diario" },
            cuentas: { label: "Plan de Cuentas" },
            mayor: { label: "Mayor General" },
        },
    },
    facturacion: {
        label: "Facturación",
        submodules: {
            emision: { label: "Emisión de Documentos" },
            recepcion: { label: "Recepción de Documentos" },
        },
    },
    reportes: {
        label: "Reportes",
        submodules: {
            balance: { label: "Balance General" },
            resultados: { label: "Estado de Resultados" },
            "flujo-efectivo": { label: "Flujo de Efectivo" },
        },
    },
    configuracion: {
        label: "Configuración",
        submodules: {
            "calendario-fiscal": { label: "Calendario Fiscal" },
            "autoridad-tributaria": { label: "Autoridad Tributaria" },
            perfil: { label: "Perfil" },
            usuarios: { label: "Usuarios" },
            roles: { label: "Roles" },
        },
    },
};
/**
 * All available permissions in the system.
 * This is the source of truth for what permissions exist.
 */
export const ALL_PERMISSIONS = Object.entries(MODULES).flatMap(([module]) => Object.keys(MODULES[module].submodules).flatMap((submodule) => ACTIONS.map((action) => `${module}:${submodule}:${action}`)));
/**
 * Check if a user has a specific permission.
 * Supports wildcard matching.
 *
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermission - The permission to check for
 * @returns true if user has the permission (including via wildcard)
 */
export function hasPermission(userPermissions, requiredPermission) {
    const [reqModule, reqSubmodule, reqAction] = requiredPermission.split(":");
    return userPermissions.some((perm) => {
        const [permModule, permSubmodule, permAction] = perm.split(":");
        // Check module match (exact or wildcard)
        const moduleMatch = permModule === "*" || permModule === reqModule;
        if (!moduleMatch)
            return false;
        // Check submodule match (exact or wildcard)
        const submoduleMatch = permSubmodule === "*" || permSubmodule === reqSubmodule;
        if (!submoduleMatch)
            return false;
        // Check action match (exact or wildcard)
        const actionMatch = permAction === "*" || permAction === reqAction;
        return actionMatch;
    });
}
/**
 * Check if user has ANY of the required permissions
 */
export function hasAnyPermission(userPermissions, requiredPermissions) {
    return requiredPermissions.some((perm) => hasPermission(userPermissions, perm));
}
/**
 * Check if user has ALL of the required permissions
 */
export function hasAllPermissions(userPermissions, requiredPermissions) {
    return requiredPermissions.every((perm) => hasPermission(userPermissions, perm));
}
/**
 * Expand wildcard permissions to explicit permissions.
 * Useful for UI display or debugging.
 *
 * @param permission - Permission string (may contain wildcards)
 * @returns Array of explicit permissions
 */
export function expandPermission(permission) {
    const parts = permission.split(":");
    const module = parts[0] ?? "*";
    const submodule = parts[1] ?? "*";
    const action = parts[2] ?? "*";
    const modules = module === "*" ? Object.keys(MODULES) : [module];
    const actions = action === "*" ? [...ACTIONS] : [action];
    const result = [];
    for (const m of modules) {
        if (!(m in MODULES))
            continue;
        const submodules = submodule === "*"
            ? Object.keys(MODULES[m].submodules)
            : [submodule];
        for (const s of submodules) {
            for (const a of actions) {
                result.push(`${m}:${s}:${a}`);
            }
        }
    }
    return result;
}
/**
 * Default permissions granted to ALL authenticated users.
 * These are the baseline permissions that every user gets regardless of roles.
 * This ensures users always have access to basic navigation and their profile.
 */
export const DEFAULT_AUTHENTICATED_PERMISSIONS = [
    "configuracion:perfil:read",
    "configuracion:perfil:write",
];
/**
 * Default role permission mappings.
 * These can be overridden in the database but serve as sensible defaults.
 * Note: All users also receive DEFAULT_AUTHENTICATED_PERMISSIONS.
 */
export const DEFAULT_ROLE_PERMISSIONS = {
    administrador: ["*:*:*"], // Full access
    contador: [
        "contabilidad:*:*",
        "facturacion:*:read",
        "reportes:*:*",
        "configuracion:perfil:*",
    ],
    digitador: [
        "contabilidad:diario:read",
        "contabilidad:diario:write",
        "contabilidad:cuentas:read",
        "facturacion:emision:read",
        "facturacion:emision:write",
        "configuracion:perfil:read",
        "configuracion:perfil:write",
    ],
    facturador: [
        "facturacion:*:*",
        "contabilidad:cuentas:read",
        "reportes:*:read",
        "configuracion:perfil:*",
    ],
    lector: ["*:*:read"], // Read-only access to everything
    proveedor: [
        "facturacion:recepcion:read",
        "configuracion:perfil:read",
        "configuracion:perfil:write",
    ],
};
/**
 * Route to permission mapping.
 * Maps frontend routes to required permissions.
 *
 * Routes NOT in this map are accessible to all authenticated users.
 * The dashboard ("/") is intentionally not mapped - it's accessible to everyone.
 */
export const ROUTE_PERMISSIONS = {
    // Dashboard is accessible to all authenticated users (no permission required)
    // "/": not mapped intentionally
    // Contabilidad
    "/contabilidad/diario": "contabilidad:diario:read",
    "/contabilidad/cuentas": "contabilidad:cuentas:read",
    "/contabilidad/mayor": "contabilidad:mayor:read",
    // Facturación
    "/facturacion/emision": "facturacion:emision:read",
    "/facturacion/recepcion": "facturacion:recepcion:read",
    // Reportes
    "/reportes/balance": "reportes:balance:read",
    "/reportes/resultados": "reportes:resultados:read",
    "/reportes/flujo-efectivo": "reportes:flujo-efectivo:read",
    // Configuración
    "/configuracion/calendario-fiscal": "configuracion:calendario-fiscal:read",
    "/configuracion/autoridad-tributaria": "configuracion:autoridad-tributaria:read",
    "/configuracion/perfil": "configuracion:perfil:read",
    "/configuracion/usuarios": "configuracion:usuarios:read",
    "/configuracion/roles": "configuracion:roles:read",
};
/**
 * Get the permission required for a route
 */
export function getRoutePermission(route) {
    return ROUTE_PERMISSIONS[route];
}
/**
 * Check if user can access a route
 */
export function canAccessRoute(userPermissions, route) {
    const requiredPermission = getRoutePermission(route);
    if (!requiredPermission) {
        // No permission defined = public route
        return true;
    }
    return hasPermission(userPermissions, requiredPermission);
}
//# sourceMappingURL=index.js.map